import { fetchESV } from '../components/VerseFetcher/bibleVersions/FetchESV';
import { fetchFromBibleApi } from '../components/VerseFetcher/bibleVersions/fetchFromBibleAPI';
import { BibleVersion, isValidBibleVersion } from '../models';

export class CongratulationsService {
  private static readonly CONGRATULATIONS_REFERENCES = [
    'Deut 8:3',
    'Job 23:12',
    'Ps 19:10',
    'Ps 119:103',
    'Ps 119:105',
    'Ps 119:127',
    'Ps 119:130',
    'Ps 119:162',
    'Prov 6:23',
    'Isa 55:11',
    'Jer 15:16',
    'Acts 20:32',
    'Heb 4:12',
    '1 Pet 2:2',
    'Rom 10:17',
  ];

  /**
   * Get a random encouraging Bible verse for congratulations
   */
  static async getRandomCongratulationsVerse(
    version: BibleVersion = 'ESV'
  ): Promise<{ verseContent: string; reference?: string }> {
    try {
      // Validate version
      if (!isValidBibleVersion(version)) {
        throw new Error(`Invalid Bible version: ${version}`);
      }

      // Select random reference
      const randomReference =
        this.CONGRATULATIONS_REFERENCES[
          Math.floor(Math.random() * this.CONGRATULATIONS_REFERENCES.length)
        ];

      // Fetch verse using the user's preferred version
      const verseContent = await this.fetchVerse(randomReference, version);

      return { verseContent, reference: randomReference };
    } catch (error) {
      console.error('Error fetching congratulations verse:', error);
      return { verseContent: this.getFallbackMessage() };
    }
  }

  /**
   * Fetch a verse using the specified Bible version
   */
  private static async fetchVerse(
    reference: string,
    version: BibleVersion
  ): Promise<string> {
    try {
      let result: string;

      switch (version.toUpperCase()) {
        case 'WEB':
          const webData = await fetchFromBibleApi(reference, 'WEB');
          result = webData.content.map((verse: any) => verse.text).join(' ');
          break;
        case 'KJV':
          const kjvData = await fetchFromBibleApi(reference, 'KJV');
          result = kjvData.content.map((verse: any) => verse.text).join(' ');
          break;
        case 'ASV':
          const asvData = await fetchFromBibleApi(reference, 'ASV');
          result = asvData.content.map((verse: any) => verse.text).join(' ');
          break;
        case 'ESV':
        default:
          // For ESV, exclude verse numbers for cleaner congratulations display
          result = await fetchESV(
            reference,
            {
              'include-verse-numbers': 'false',
              'include-short-copyright': 'false',
              'include-first-verse-numbers': 'false',
              'include-footnote-body': 'false',
              'include-headings': 'false',
              'include-chapter-numbers': 'false',
            },
            true
          );
          break;
      }

      // Clean the HTML and extract plain text
      return result || this.getFallbackMessage();
    } catch (error) {
      console.error(`Error fetching ${version} verse:`, error);
      return this.getFallbackMessage();
    }
  }

  /**
   * Get a fallback message when verse fetching fails
   */
  private static getFallbackMessage(): string {
    return 'Scripture is the foundation of faith.';
  }

  /**
   * Get all available congratulations references (for debugging/testing)
   */
  static getAvailableReferences(): string[] {
    return [...this.CONGRATULATIONS_REFERENCES];
  }
}
